import { useEffect, useRef } from 'react';

/**
 * A stream attached to an <audio> element, with a volume this component owns.
 *
 * `muted` exists for the original remote voice, which section 3 says must not
 * play unless the listener turns it on. Muting rather than disconnecting keeps
 * the element primed, so enabling it is instant and does not re-trip autoplay.
 */
export function AudioSink({
  stream,
  volume,
  muted = false,
  onBlocked,
}: {
  stream: MediaStream | null;
  volume: number;
  muted?: boolean;
  onBlocked?: (blocked: boolean) => void;
}) {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    if (!stream) return;

    // Autoplay can still be refused even after a click elsewhere on the page.
    // The caller shows a button rather than leaving a silent screen.
    el.play().then(
      () => onBlocked?.(false),
      () => onBlocked?.(true),
    );
  }, [stream, onBlocked]);

  useEffect(() => {
    const el = ref.current;
    if (el) el.volume = Math.max(0, Math.min(1, volume));
  }, [volume]);

  return <audio ref={ref} autoPlay playsInline muted={muted} />;
}
